import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { lookupPlanPrice, lookupTruckersPrice, sumAddOnsPrice, getBasePrice, fetchLivePricing } from "../../lib/pricing";

export default function PlanPricingModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [planSystem, setPlanSystem] = useState("meal-plans");
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [values, setValues] = useState({
    planType: "",
    planTerm: "",
    nvPref: "",
    billingTerm: "Weekly",
    addOns: [],
    truckersType: "",
    truckersTerm: ""
  });

  useEffect(() => {
    if (isOpen && !pricingLoaded) {
      fetchLivePricing().then(() => {
        setPricingLoaded(true);
      });
    }
  }, [isOpen, pricingLoaded]);

  const handlePlanSystemChange = (system) => {
    setPlanSystem(system);
    setValues({
      planType: "",
      planTerm: "",
      nvPref: "",
      billingTerm: "Weekly",
      addOns: [],
      truckersType: "",
      truckersTerm: ""
    });
  };

  const handleChange = (field, value) => {
    setValues(prev => {
      const newValues = { ...prev, [field]: value };
      if (field === "planType") {
        newValues.planTerm = "";
        newValues.nvPref = "";
      }
      if (field === "planTerm") {
        newValues.nvPref = "";
      }
      if (field === "truckersType") {
        newValues.truckersTerm = "";
      }
      return newValues;
    });
  };

  const handleAddOnToggle = (addon) => {
    setValues(prev => ({
      ...prev,
      addOns: prev.addOns.includes(addon)
        ? prev.addOns.filter(a => a !== addon)
        : [...prev.addOns, addon]
    }));
  };

  const planTermOptions = {
    "2 Meals a Day": ["5 Days Veg", "5 Days Non-Veg", "2 Days Non-Veg, 3 Days Veg"],
    "3 Meals a Day": ["5 Days Veg", "5 Days Non-Veg", "2 Days Non-Veg, 3 Days Veg"]
  };

  const nvPrefOptions = {
    "2 Meals a Day": {
      "5 Days Non-Veg": ["1 NV + 1 Veg", "Both NV"],
      "2 Days Non-Veg, 3 Days Veg": ["1 NV + 1 Veg", "Both NV"]
    },
    "3 Meals a Day": {
      "5 Days Non-Veg": ["1 NV/day", "2 NV/day"],
      "2 Days Non-Veg, 3 Days Veg": ["1 NV/day", "2 NV/day"]
    }
  };

  const truckersTermOptions = {
    "Truckers 10 Meals a Week": ["5 Veg + 5 Non-Veg", "10 Veg Meals", "10 Non-Veg Meals"],
    "Truckers 12 Meals a Week": ["6 Veg + 6 Non-Veg", "12 Veg Meals"]
  };

  const showNVPref = planSystem === "meal-plans" && values.planTerm && values.planTerm.includes("Non-Veg") && values.planTerm !== "5 Days Veg";
  const currentNVPrefOptions = values.planType && values.planTerm ? nvPrefOptions[values.planType]?.[values.planTerm] || [] : [];

  let planPrice = 0;
  let addOnsTotal = 0;
  let hasSelection = false;

  if (planSystem === "meal-plans") {
    planPrice = lookupPlanPrice(values.planType, values.planTerm, values.nvPref, values.billingTerm);
    addOnsTotal = sumAddOnsPrice(values.addOns, values.billingTerm);
    hasSelection = values.planType && values.planTerm;
  } else {
    planPrice = lookupTruckersPrice(values.truckersType, values.truckersTerm);
    addOnsTotal = sumAddOnsPrice(values.addOns, "Weekly");
    hasSelection = values.truckersType && values.truckersTerm;
  }

  const total = planPrice + addOnsTotal;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-orange-500">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 px-8 py-6 flex items-center justify-between rounded-t-3xl z-10 shadow-2xl">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Plan & Pricing</h2>
            <p className="text-orange-100 text-sm md:text-base mt-1">Select your plan to see pricing before HST</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-orange-200 transition-all hover:rotate-90 duration-300 bg-white/10 hover:bg-white/20 p-2 rounded-full"
          >
            <X className="w-6 h-6 md:w-7 md:h-7" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6">
          {/* Step 1: Plan System Switch */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-xl border border-orange-500/30">
            <Label className="mb-4 block text-lg font-bold text-white flex items-center gap-2">
              <span className="bg-orange-500 text-white px-2 py-1 rounded text-sm">1</span>
              Choose Your Plan System
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                variant={planSystem === "meal-plans" ? "default" : "outline"}
                className={`h-16 text-lg font-semibold transition-all duration-300 ${
                  planSystem === "meal-plans"
                    ? "bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 shadow-lg shadow-orange-500/50 text-white scale-105"
                    : "border-2 border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-500 bg-gray-800/50"
                }`}
                onClick={() => handlePlanSystemChange("meal-plans")}
              >
                Meal Plans
              </Button>
              <Button
                type="button"
                variant={planSystem === "truckers" ? "default" : "outline"}
                className={`h-16 text-lg font-semibold transition-all duration-300 ${
                  planSystem === "truckers"
                    ? "bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 shadow-lg shadow-orange-500/50 text-white scale-105"
                    : "border-2 border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-500 bg-gray-800/50"
                }`}
                onClick={() => handlePlanSystemChange("truckers")}
              >
                Truckers
              </Button>
            </div>
          </div>

          {/* Step 2: Meal Plans Config */}
          {planSystem === "meal-plans" && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-xl border border-orange-500/30 space-y-5">
              <Label className="mb-2 block text-lg font-bold text-white flex items-center gap-2">
                <span className="bg-orange-500 text-white px-2 py-1 rounded text-sm">2</span>
                Configure Your Meal Plan
              </Label>

              <div>
                <Label className="text-gray-300 mb-2 block font-semibold">Plan Type *</Label>
                <Select value={values.planType} onValueChange={(v) => handleChange('planType', v)}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                    <SelectValue placeholder="Select plan type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2 Meals a Day">2 Meals a Day</SelectItem>
                    <SelectItem value="3 Meals a Day">3 Meals a Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {values.planType && (
                <div className="animate-fadeIn">
                  <Label className="text-gray-300 mb-2 block font-semibold">Plan Term *</Label>
                  <Select value={values.planTerm} onValueChange={(v) => handleChange('planTerm', v)}>
                    <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                      <SelectValue placeholder="Select plan term..." />
                    </SelectTrigger>
                    <SelectContent>
                      {planTermOptions[values.planType]?.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showNVPref && currentNVPrefOptions.length > 0 && (
                <div className="animate-fadeIn">
                  <Label className="text-gray-300 mb-2 block font-semibold">Non-Veg Preference *</Label>
                  <Select value={values.nvPref} onValueChange={(v) => handleChange('nvPref', v)} required>
                    <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                      <SelectValue placeholder="Select preference" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentNVPrefOptions.map(pref => (
                        <SelectItem key={pref} value={pref}>{pref}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-gray-300 mb-2 block font-semibold">Billing Term *</Label>
                <Select value={values.billingTerm} onValueChange={(v) => handleChange('billingTerm', v)}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Truckers Config */}
          {planSystem === "truckers" && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-xl border border-orange-500/30 space-y-5">
              <Label className="mb-2 block text-lg font-bold text-white flex items-center gap-2">
                <span className="bg-orange-500 text-white px-2 py-1 rounded text-sm">2</span>
                Configure Your Trucker Plan
              </Label>

              <div>
                <Label className="text-gray-300 mb-2 block font-semibold">Truckers Plan Type *</Label>
                <Select value={values.truckersType} onValueChange={(v) => handleChange('truckersType', v)}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                    <SelectValue placeholder="Select truckers plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Truckers 10 Meals a Week">Truckers 10 Meals a Week</SelectItem>
                    <SelectItem value="Truckers 12 Meals a Week">Truckers 12 Meals a Week</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {values.truckersType && (
                <div className="animate-fadeIn">
                  <Label className="text-gray-300 mb-2 block font-semibold">Truckers Plan Term *</Label>
                  <Select value={values.truckersTerm} onValueChange={(v) => handleChange('truckersTerm', v)}>
                    <SelectTrigger className="bg-gray-900 border-gray-700 text-white hover:border-orange-500 transition-colors h-12">
                      <SelectValue placeholder="Select meal distribution..." />
                    </SelectTrigger>
                    <SelectContent>
                      {truckersTermOptions[values.truckersType]?.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-gray-300 mb-2 block font-semibold">Billing Term</Label>
                <div className="px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 text-gray-300">
                  Weekly (Truckers plans are weekly only)
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Add-ons */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-xl border border-orange-500/30">
            <Label className="mb-4 block text-lg font-bold text-white flex items-center gap-2">
              <span className="bg-orange-500 text-white px-2 py-1 rounded text-sm">3</span>
              Add-ons (optional)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["5 Eggs Daily", "1 Veg Meal Daily", "1 Non Veg Meal Daily", "Bulk Me Up", "Protein Balls (2)", "Fruit on the Side"].map(addon => (
                <label
                  key={addon}
                  className={`flex items-center gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                    values.addOns.includes(addon)
                      ? "bg-orange-500 border-orange-400 shadow-lg shadow-orange-500/30"
                      : "bg-gray-900 border-gray-700 hover:border-orange-500"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={values.addOns.includes(addon)}
                    onChange={() => handleAddOnToggle(addon)}
                    className="w-5 h-5 accent-orange-500"
                  />
                  <span className={`font-medium ${values.addOns.includes(addon) ? "text-white" : "text-gray-300"}`}>
                    {addon}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Preview - Without HST */}
          {hasSelection && (
            <div className="mt-6 rounded-2xl border-2 border-orange-500 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-600 p-8 shadow-2xl shadow-orange-500/50 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-white text-2xl md:text-3xl flex items-center gap-2">
                  Your Price Preview
                </h3>
                <span className="text-xs md:text-sm font-semibold text-orange-100 bg-white/20 px-3 py-1 rounded-full">
                  Before HST
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center bg-white/20 backdrop-blur-sm p-4 rounded-xl transition-all hover:bg-white/30">
                  <span className="font-semibold text-white">{planSystem === "meal-plans" ? "Meal Plan:" : "Trucker Plan:"}</span>
                  <strong className="text-2xl text-white">${getBasePrice(planPrice)}</strong>
                </div>
                <div className="flex justify-between items-center bg-white/20 backdrop-blur-sm p-4 rounded-xl transition-all hover:bg-white/30">
                  <span className="font-semibold text-white">Add-ons:</span>
                  <strong className="text-2xl text-white">${getBasePrice(addOnsTotal)}</strong>
                </div>
                <div className="h-px bg-white/30 my-2"></div>
                <div className="flex justify-between items-center bg-white p-5 rounded-xl text-orange-600 shadow-lg">
                  <span className="text-xl md:text-2xl font-bold">Total (Before HST):</span>
                  <span className="text-3xl md:text-4xl font-bold">${getBasePrice(total)}</span>
                </div>
              </div>

              <div className="mt-6 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                <p className="text-sm text-white flex items-start gap-2">
                  <span className="text-xl">ℹ️</span>
                  <span>
                    All prices are exclusive of HST (13%). <strong>HST will be calculated during checkout.</strong>
                    {planSystem === "truckers" && <span className="block mt-2">Trucker plans are billed weekly only.</span>}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center pt-2">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-6 text-xl font-bold shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
              onClick={() => { onClose(); navigate('/signup/meal-plans'); }}
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
